import { useEffect, useState } from 'react'
import { Check, ImageOff, Sparkles } from 'lucide-react'

import { cn } from 'lib/utils'

import { getSourcePhotos } from '../services/presentationService'
import type { EntityRef, SourcePhoto } from '../types/presentation.types'

/**
 * Pick from the source location's own photos (MYD-499), for a `photo` field
 * or a list of them.
 *
 * Nothing picked is a real choice, not a gap: the slide then shows the
 * newest photo(s), so a deck made today stays current as photos are added.
 * That is why the first tile is "Newest" rather than an empty state.
 *
 * The photos come from the server already filtered to ones a slide will show
 * (Cloudinary only) and to a source this user may see, so anything offered
 * here will render.
 */

type PhotosState = { status: 'loading' } | { status: 'ready'; photos: SourcePhoto[] } | { status: 'failed' }

/**
 * One request per source while the editor is open. Every photo field on the
 * inspector for a source shares it, and reopening a block does not refetch.
 */
const cache = new Map<string, Promise<SourcePhoto[]>>()

export const clearPhotoCache = () => cache.clear()

const photosFor = (source: EntityRef) => {
	const key = `${source.scope}:${source.entityId}`
	if (!cache.has(key)) {
		const request = getSourcePhotos(source)
		// A failure is not cached, so the next open tries again.
		request.catch(() => cache.delete(key))
		cache.set(key, request)
	}
	return cache.get(key) as Promise<SourcePhoto[]>
}

function useSourcePhotos(source: EntityRef | null): PhotosState | null {
	const [state, setState] = useState<PhotosState>({ status: 'loading' })
	const key = source ? `${source.scope}:${source.entityId}` : null

	useEffect(() => {
		if (!source) return
		let cancelled = false
		setState({ status: 'loading' })
		photosFor(source).then(
			photos => !cancelled && setState({ status: 'ready', photos }),
			() => !cancelled && setState({ status: 'failed' }),
		)
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key])

	return source ? state : null
}

interface PhotoFieldProps {
	id: string
	source: EntityRef | null
	/** One id for a single photo, a list for a grid. */
	value: string | string[] | undefined
	onChange: (next: unknown) => void
	/** Set for a list: how many may be picked. */
	max?: number
}

export function PhotoField({ id, source, value, onChange, max }: PhotoFieldProps) {
	const state = useSourcePhotos(source)
	const multiple = max !== undefined
	const picked = multiple ? (Array.isArray(value) ? value : []) : typeof value === 'string' ? [value] : []

	if (!state) return <p className="text-xs text-muted-foreground">Choose an asset for this block first.</p>
	if (state.status === 'loading') return <div className="h-16 animate-pulse rounded-md bg-gray-100" aria-label="Loading photos" />
	if (state.status === 'failed') return <p className="text-xs text-destructive">The photos could not be loaded.</p>
	if (state.photos.length === 0) {
		return (
			<p className="flex items-center gap-1.5 text-xs text-muted-foreground">
				<ImageOff className="h-3.5 w-3.5" /> This location has no photos yet.
			</p>
		)
	}

	const toggle = (photoId: string) => {
		if (!multiple) return onChange(picked[0] === photoId ? undefined : photoId)
		if (picked.includes(photoId)) return onChange(picked.filter(each => each !== photoId))
		if (picked.length < (max ?? 0)) onChange([...picked, photoId])
	}

	const atCap = multiple && picked.length >= (max ?? 0)

	return (
		<div className="space-y-1.5">
			<div id={id} role="group" aria-label="Photos" className="grid grid-cols-4 gap-1.5">
				<button
					type="button"
					onClick={() => onChange(multiple ? [] : undefined)}
					aria-pressed={picked.length === 0}
					title={multiple ? 'Show the newest photos' : 'Show the newest photo'}
					className={cn(
						'flex aspect-square flex-col items-center justify-center gap-0.5 rounded-md border text-[10px] font-medium',
						picked.length === 0 ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-500 hover:bg-gray-50',
					)}
				>
					<Sparkles className="h-3.5 w-3.5" />
					Newest
				</button>
				{state.photos.map(photo => {
					const order = picked.indexOf(photo._id)
					const selected = order >= 0
					return (
						<button
							key={photo._id}
							type="button"
							onClick={() => toggle(photo._id)}
							disabled={!selected && atCap}
							aria-pressed={selected}
							aria-label={photo.caption || photo.name || 'Photo'}
							title={photo.caption || photo.name || undefined}
							className={cn(
								'relative aspect-square overflow-hidden rounded-md border-2 disabled:opacity-40',
								selected ? 'border-primary' : 'border-transparent hover:border-gray-300',
							)}
						>
							<img src={photo.url} alt="" loading="lazy" className="h-full w-full object-cover" draggable={false} />
							{selected && (
								<span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white">
									{multiple ? order + 1 : <Check className="h-3 w-3" />}
								</span>
							)}
						</button>
					)
				})}
			</div>
			{multiple && (
				<p className="text-[11px] text-muted-foreground">
					{picked.length === 0 ? 'Showing the newest. Pick photos to choose them, in order.' : `${picked.length} of up to ${max} picked, in the order shown.`}
				</p>
			)}
		</div>
	)
}

export default PhotoField
